import { useEffect, useState } from "react";
import api from "../../../../../utils/axiosInstance";
import FormInput from "../../../../../components/Form/FormInput";
import FormSelect from "../../../../../components/Form/FormSelect";
import FormTextarea from "../../../../../components/Form/TextArea";

const PriceListForm = ({ clientId, editingItem, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    client_id: clientId,
    service_id: "",
    language_pair_id: "",
    specialization_id: "",
    currency_id: "",
    unit: "",
    price_per_unit: "",
    note: "",
  });

  const [errors, setErrors] = useState({});
  const [services, setServices] = useState([]);
  const [languagePairs, setLanguagePairs] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [units, setUnits] = useState([]);

  useEffect(() => {
    fetchDropdowns();
    if (editingItem) {
      setFormData({
        client_id: clientId,
        service_id: editingItem.service_id,
        language_pair_id: editingItem.language_pair_id,
        specialization_id: editingItem.specialization_id,
        currency_id: editingItem.currency_id,
        unit: editingItem.unit,
        price_per_unit: editingItem.price_per_unit,
        note: editingItem.note || "",
      });
    }
  }, [editingItem, clientId]);

  const fetchDropdowns = async () => {
    try {
      const [dataRes, cur, unt] = await Promise.all([
        api.get(`/client-price-list/client/${clientId}/price-list`),
        api.get(`/admin-currencies`),
        api.get(`/admin-units`),
      ]);

      const dropdowns = dataRes.data.data?.dropdowns || {};

      setServices(Array.isArray(dropdowns.services) ? dropdowns.services : []);
      setLanguagePairs(Array.isArray(dropdowns.languagePairs) ? dropdowns.languagePairs : []);
      setSpecializations(Array.isArray(dropdowns.specializations) ? dropdowns.specializations : []);

      const currenciesData = cur.data.data || [];
      setCurrencies(Array.isArray(currenciesData) ? currenciesData : []);

      const unitsData = unt.data.data || [];
      setUnits(Array.isArray(unitsData) ? unitsData : []);
    } catch (err) {
      console.error("Failed to load dropdowns", err);
    }
  };

  const requiredFields = [
    "service_id",
    "language_pair_id",
    "specialization_id",
    "currency_id",
    "unit",
    "price_per_unit",
  ];

  const fieldLabels = {
    service_id: "Service",
    language_pair_id: "Language Pair",
    specialization_id: "Specialization",
    currency_id: "Currency",
    unit: "Unit",
    price_per_unit: "Price per Unit",
  };

  const validate = () => {
    const currentErrors = {};
    requiredFields.forEach((field) => {
      if (!formData[field] && formData[field] !== 0) {
        currentErrors[field] = `${fieldLabels[field]} is required.`;
      }
    });
    setErrors(currentErrors);
    return Object.keys(currentErrors).length === 0;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let errorMsg = "";
    if (requiredFields.includes(name) && !value) {
      errorMsg = `${fieldLabels[name]} is required.`;
    }
    setErrors((prev) => ({ ...prev, [name]: errorMsg }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSave(formData);
  };

  const serviceOptions = services.map((s) => ({ value: s.id, label: s.name }));
  const languagePairOptions = languagePairs.map((lp) => ({
    value: lp.id,
    label: `${lp.sourceLanguage?.code || "?"} → ${lp.targetLanguage?.code || "?"}`,
  }));
  const specializationOptions = specializations.map((sp) => ({
    value: sp.id,
    label: sp.name,
  }));
  const currencyOptions = currencies.map((c) => ({
    value: c.id,
    label: `${c.currency?.code || ""} (${c.currency?.symbol || ""})`,
  }));
  const unitOptions = units.map((u) => ({ value: u.name, label: u.name }));

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-xl font-bold text-gray-900">
          {editingItem ? "Edit Price" : "Add New Price"}
        </h2>
      </div>
      <form
        onSubmit={handleSubmit}
        noValidate
        className="bg-white shadow-sm border border-gray-100 rounded-lg p-5 space-y-5"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4 items-start">
          <FormSelect
            label="Service"
            name="service_id"
            value={formData.service_id}
            onChange={handleChange}
            onBlur={handleBlur}
            options={serviceOptions}
            error={errors.service_id}
            required
          />

          <FormSelect
            label="Language Pair"
            name="language_pair_id"
            value={formData.language_pair_id}
            onChange={handleChange}
            onBlur={handleBlur}
            options={languagePairOptions}
            error={errors.language_pair_id}
            required
          />

          <FormSelect
            label="Specialization"
            name="specialization_id"
            value={formData.specialization_id}
            onChange={handleChange}
            onBlur={handleBlur}
            options={specializationOptions}
            error={errors.specialization_id}
            required
          />

          <FormSelect
            label="Currency"
            name="currency_id"
            value={formData.currency_id}
            onChange={handleChange}
            onBlur={handleBlur}
            options={currencyOptions}
            error={errors.currency_id}
            required
          />

          <FormSelect
            label="Unit"
            name="unit"
            value={formData.unit}
            onChange={handleChange}
            onBlur={handleBlur}
            options={unitOptions}
            error={errors.unit}
            required
          />

          <FormInput
            label="Price per Unit"
            name="price_per_unit"
            type="number"
            value={formData.price_per_unit}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.price_per_unit}
            required
          />

          <div className="md:col-span-2">
            <FormTextarea
              label="Note"
              name="note"
              value={formData.note}
              onChange={handleChange}
              rows={3}
              placeholder="Optional notes..."
            />
          </div>
        </div>

        <div className="pt-3 border-t border-gray-100 space-y-3">
          <span className="block text-xs text-gray-500">
            Fields marked with <span className="text-red-500">*</span> are mandatory.
          </span>
          <div className="flex gap-2 text-sm">
            <button
              type="submit"
              className="px-3 py-1.5 font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
            >
              {editingItem ? "Update" : "Create"}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="px-3 py-1.5 font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded"
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PriceListForm;
